import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { CheckCircle, Help, Visibility, VisibilityOff } from "@mui/icons-material";
import { confirmResetPassword, resetPassword } from "aws-amplify/auth";

import logoLogin123Zap from "../../shared/assets/images/logo-login-123zap.png";
import themeColors from "../../shared/components/ThemeColors/ThemeColors";

function getResetErrorMessage(error) {
  switch (error.name || error.code) {
    case "UserNotFoundException":
      return "Usuário não encontrado.";
    case "LimitExceededException":
      return "Muitas tentativas. Tente novamente mais tarde.";
    case "TooManyRequestsException":
      return "Muitas solicitações. Aguarde antes de tentar novamente.";
    default:
      return "Erro ao redefinir a senha. Tente novamente.";
  }
}

export default function ResetRequiredPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const username = searchParams.get("username");

  const [confirmationCode, setConfirmationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [checkingUser, setCheckingUser] = useState(true);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [timer, setTimer] = useState(30);

  useEffect(() => {
    if (!username) {
      navigate("/login");
      return undefined;
    }

    let active = true;

    async function checkUserStatus() {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_AWS_URL}/auth/user-status`,
          {
            method: "POST",
            body: JSON.stringify({ user_identifier: username }),
            headers: { "Content-Type": "application/json" },
          },
        );
        const data = await response.json();

        if (!active) return;

        if (!data || data?.user_status?.status !== "RESET_REQUIRED") {
          navigate("/login");
          return;
        }

        setCheckingUser(false);
      } catch (error) {
        console.error("Erro ao verificar status do usuário:", error);
        if (active) navigate("/login");
      }
    }

    checkUserStatus();

    return () => {
      active = false;
    };
  }, [navigate, username]);

  useEffect(() => {
    if (timer === 0) return undefined;

    const timerId = window.setTimeout(() => {
      setTimer((current) => current - 1);
    }, 1000);

    return () => window.clearTimeout(timerId);
  }, [timer]);

  async function confirmPasswordReset() {
    setLoading(true);
    setMessage(null);

    try {
      await confirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      });

      setMessage({
        severity: "success",
        text: "Senha redefinida com sucesso. Faça login com sua nova senha.",
      });

      window.setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error) {
      setMessage({ severity: "error", text: getResetErrorMessage(error) });
    } finally {
      setLoading(false);
    }
  }

  async function resendCode() {
    setLoading(true);
    setMessage(null);

    try {
      await resetPassword({ username });
      setTimer(30);
      setMessage({
        severity: "success",
        text: "Código de verificação reenviado com sucesso.",
      });
    } catch (error) {
      setMessage({
        severity: "error",
        text: "Erro ao reenviar o código de verificação.",
      });
    } finally {
      setLoading(false);
    }
  }

  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const canSubmit =
    confirmationCode &&
    newPassword &&
    confirmPassword &&
    newPassword === confirmPassword &&
    !loading;

  if (checkingUser) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        py: 4,
        bgcolor: "#fff",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 380 }}>
        <Box
          component="img"
          src={logoLogin123Zap}
          alt="123zap"
          sx={{ display: "block", width: 74, height: "auto", mb: 4 }}
        />

        <Typography sx={titleSx}>Redefinir senha</Typography>
        <Typography sx={subtitleSx}>
          Informe o código enviado por e-mail e escolha uma nova senha.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 3 }}>
          {message ? (
            <Alert severity={message.severity}>{message.text}</Alert>
          ) : null}

          <TextField
            label={
              <Box component="span" sx={{ display: "inline-flex", gap: 0.5 }}>
                Código de confirmação
                <Tooltip title="Verifique seu e-mail para o código de confirmação.">
                  <Help sx={{ fontSize: 16 }} />
                </Tooltip>
              </Box>
            }
            value={confirmationCode}
            onChange={(event) => setConfirmationCode(event.target.value)}
            disabled={loading}
            fullWidth
            autoFocus
            sx={fieldSx}
          />

          <TextField
            label={
              <Box component="span" sx={{ display: "inline-flex", gap: 0.5 }}>
                Nova senha
                <Tooltip title="Use pelo menos 8 caracteres, com letras, números e símbolos.">
                  <Help sx={{ fontSize: 16 }} />
                </Tooltip>
                <Tooltip title={passwordsMatch ? "Senhas coincidem" : "Senhas não coincidem"}>
                  <CheckCircle
                    sx={{
                      fontSize: 16,
                      color: passwordsMatch ? "#4CAF50" : "#D1D5DB",
                    }}
                  />
                </Tooltip>
              </Box>
            }
            type={showPassword ? "text" : "password"}
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            disabled={loading}
            fullWidth
            autoComplete="new-password"
            sx={fieldSx}
            InputProps={passwordAdornment(showPassword, setShowPassword)}
          />

          <TextField
            label="Confirmar senha"
            type={showPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            disabled={loading}
            fullWidth
            autoComplete="new-password"
            sx={fieldSx}
            InputProps={passwordAdornment(showPassword, setShowPassword)}
          />

          <Button
            variant="contained"
            fullWidth
            disabled={!canSubmit}
            onClick={confirmPasswordReset}
            sx={buttonSx}
          >
            Confirmar
          </Button>
          <Button
            variant="outlined"
            fullWidth
            disabled={timer > 0 || loading}
            onClick={resendCode}
            sx={outlineButtonSx}
          >
            Reenviar código {timer > 0 ? `(${timer}s)` : ""}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function passwordAdornment(showPassword, setShowPassword) {
  return {
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
          edge="end"
          onClick={() => setShowPassword((current) => !current)}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  };
}

const titleSx = {
  color: "#141827",
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontSize: 32,
  fontWeight: 800,
  lineHeight: 1.25,
};

const subtitleSx = {
  mt: 1.5,
  color: "#667085",
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontSize: 16,
  lineHeight: 1.5,
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    bgcolor: "#F9F9F9",
    "& fieldset": { borderColor: "#D0D5DD" },
    "&:hover fieldset": { borderColor: themeColors.purple400 },
    "&.Mui-focused fieldset": { borderColor: themeColors.purple500 },
  },
};

const buttonSx = {
  minHeight: 46,
  borderRadius: "999px",
  bgcolor: themeColors.purple600,
  boxShadow: "none",
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontWeight: 700,
  textTransform: "none",
  "&:hover": { bgcolor: themeColors.purple700, boxShadow: "none" },
};

const outlineButtonSx = {
  minHeight: 46,
  borderRadius: "999px",
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontWeight: 700,
  textTransform: "none",
};
